import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { Badge, Button, Card, Text, Title } from '@tremor/react';
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';

const API_HOST = process.env.REACT_APP_API_HOST;

type ReportSchedule = {
  system: { id: string; company: string; created_by: string };
  details: {
    name: string;
    report_types: string[];
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
    enabled: boolean;
    next_run: string;
    last_run?: string;
  };
};

const frequencyLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

export default function ReportScheduler() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    report_types: [] as string[],
    frequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    recipients: '',
  });

  const { data: schedules, isLoading } = useQuery(
    'reportSchedules',
    async () => {
      const { data } = await axios.get(`${API_HOST}/v1/report-schedules`);
      return data.data as ReportSchedule[];
    },
  );

  const handleCreate = async () => {
    try {
      setError(null);
      await axios.post(`${API_HOST}/v1/report-schedules`, {
        ...formData,
        recipients: formData.recipients
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
      });
      await queryClient.invalidateQueries('reportSchedules');
      setShowForm(false);
      setFormData({
        name: '',
        report_types: [],
        frequency: 'weekly',
        recipients: '',
      });
    } catch (err) {
      console.error('Failed to create schedule', err);
      setError('Failed to create schedule. Please try again.');
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      setError(null);
      await axios.patch(`${API_HOST}/v1/report-schedules/${id}`, {
        enabled: !enabled,
      });
      await queryClient.invalidateQueries('reportSchedules');
    } catch (err) {
      console.error('Failed to toggle schedule', err);
      setError('Failed to update schedule status. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }
    try {
      setError(null);
      await axios.delete(`${API_HOST}/v1/report-schedules/${id}`);
      await queryClient.invalidateQueries('reportSchedules');
    } catch (err) {
      console.error('Failed to delete schedule', err);
      setError('Failed to delete schedule. Please try again.');
    }
  };

  const reportTypeOptions = [
    { value: 'bradford_factor', label: 'Bradford Factor' },
    { value: 'absence_trends', label: 'Absence Trends' },
    { value: 'leave_balance', label: 'Leave Balance' },
    { value: 'absenteeism_risk', label: 'Absenteeism Risk' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Title>Scheduled Reports</Title>
          <Text>Automate report delivery to your team</Text>
        </div>
        <Button onClick={() => setShowForm(!showForm)} icon={RiAddLine}>
          New Schedule
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center justify-between">
          <Text className="text-red-700">{error}</Text>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {showForm && (
        <Card className="mb-6">
          <Title className="mb-4">Create Schedule</Title>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Weekly absence report"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Types
              </label>
              <div className="flex flex-wrap gap-2">
                {reportTypeOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-1 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={formData.report_types.includes(opt.value)}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...formData.report_types, opt.value]
                          : formData.report_types.filter(
                              (t) => t !== opt.value,
                            );
                        setFormData({ ...formData, report_types: types });
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={formData.frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    frequency: e.target.value as any,
                  })
                }
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipients (comma-separated emails)
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={formData.recipients}
                onChange={(e) =>
                  setFormData({ ...formData, recipients: e.target.value })
                }
                placeholder="manager@company.com, hr@company.com"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  !formData.name ||
                  formData.report_types.length === 0 ||
                  !formData.recipients
                }
              >
                Create
              </Button>
            </div>
          </div>
        </Card>
      )}

      {isLoading ? (
        <Text>Loading schedules...</Text>
      ) : schedules?.length === 0 ? (
        <Card className="text-center py-8">
          <Text>No scheduled reports yet. Create one to get started.</Text>
        </Card>
      ) : (
        <div className="space-y-3">
          {schedules?.map((schedule) => (
            <Card
              key={schedule.system.id}
              className="flex items-center justify-between"
            >
              <div>
                <div className="flex items-center gap-2">
                  <Text className="font-medium text-gray-900">
                    {schedule.details.name}
                  </Text>
                  <Badge
                    color={schedule.details.enabled ? 'green' : 'gray'}
                  >
                    {schedule.details.enabled ? 'Active' : 'Paused'}
                  </Badge>
                  <Badge color="blue">
                    {frequencyLabels[schedule.details.frequency]}
                  </Badge>
                </div>
                <Text className="text-sm text-gray-500 mt-1">
                  Reports: {schedule.details.report_types.join(', ')} | Next
                  run:{' '}
                  {new Date(schedule.details.next_run).toLocaleString()}
                  {schedule.details.last_run &&
                    ` | Last run: ${new Date(schedule.details.last_run).toLocaleString()}`}
                </Text>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() =>
                    handleToggle(
                      schedule.system.id,
                      schedule.details.enabled,
                    )
                  }
                >
                  {schedule.details.enabled ? 'Pause' : 'Resume'}
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  color="red"
                  icon={RiDeleteBinLine}
                  onClick={() => handleDelete(schedule.system.id)}
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
